/* eslint-disable */
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useLocation, useParams } from "react-router";
import {
  Box,
  Button,
  Divider,
  IconButton,
  InputAdornment,
  Skeleton,
  Stack,
  Typography,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { evaluate, round } from "mathjs";
import { Grid } from "@mui/material";
import FormWrapper from "../common/FormWrapper";
import FormField from "../common/FormField";
import {
  fetchSections,
  OnSubmitValidator, //@info uncomment this before pushing
  pusblishTemplate,
  sectionAutoSave,
  setFormData,
  submitSection,
} from "../../slices/FbSections";
import { SectionSchema } from "../../utils/schemas/initial";
import { notify } from "../../../../bmr/utils/methods/notification";
import DeleteModal from "../../../../bmr/components/common/BaseModal/DeleteModal";
import {
  fetchTemplates,
  updateTemplateStatus,
} from "../../../../bmr/slices/formTemplate";
import dayjs from "dayjs";
import customParseFormat from "dayjs/plugin/customParseFormat";
import { fetchDynamicAPIS } from "../../../../utils/utils";
import ReactQuill from "react-quill";
import { generateValidationSchema } from "../../utils/schemas/DynamicValidation";
import { isEqual } from "lodash";

dayjs.extend(customParseFormat);

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------
const FIELD_TYPE_MAP = {
  General: "text",
  Note: "textarea",
  Validations: "text",
  Label: "label",
  Toggle: "switch",
  "Input Radio": "radio-group",
  "Input Dropdown": "select",
  "Input Checkbox": "checkbox-group",
  "Input Text": "text",
  "Input Number": "number",
  "Static Number": "number",
  "Input Email": "email",
  "Input Current Time": "time",
  "Input Date": "date",
  "Input Date and Time": "datetime",
  "Linked Field": "linked-field",
  Hyperlink: "hyperlink",
  "File Upload": "file",
  "Image Upload": "image_upload",
  Barcode: "barcode",
  "Auto Increment": "text",
};

const LAYOUT_OPTIONS = {
  12: { gridSize: 12 },
  6: { gridSize: 6 },
  4: { gridSize: 4 },
  3: { gridSize: 3 },
  2: { gridSize: 2 },
};

// Formats the API accepts in date_display_format
const VALID_DATE_FORMATS = [
  "DD/MM/YY",
  "MM/DD/YY",
  "YY/MM/DD",
  "DD-MM-YY",
  "MM-DD-YY",
  "YY-MM-DD",
  "DD/MM/YYYY",
  "YYYY-MM-DD",
  "MM",
  "YYYY",
  "MM/YYYY",
  "MM-YYYY",
  "MM/YY",
  "MM-YY",
];

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

/** Sort fields by order and group consecutive same-layout fields together. */
const buildFieldGroups = (fields) => {
  const sorted = [...fields].sort((a, b) => a.order - b.order);
  const groups = [];
  let currentLayout = null;
  let currentGroup = [];

  sorted.forEach((field) => {
    const layout = field.property.field_layout || 12;
    if (layout !== currentLayout) {
      if (currentGroup.length)
        groups.push({ layout: currentLayout, fields: currentGroup });
      currentGroup = [];
      currentLayout = layout;
    }
    currentGroup.push({
      ...field,
      type: FIELD_TYPE_MAP[field.property.field_type] || "text",
      required: field.property.general?.required || false,
    });
  });

  if (currentGroup.length)
    groups.push({ layout: currentLayout, fields: currentGroup });
  return groups;
};

const resolveDateConstraints = (inputDate = {}) => {
  const {
    past_only = false,
    future_only = false,
    min_date_enabled = false,
    max_date_enabled = false,
    min_date = "",
    max_date = "",
  } = inputDate;

  let minDate;
  if (min_date_enabled && min_date) {
    // Explicit min date provided — parse using any valid format strictly
    const parsed = dayjs(min_date, VALID_DATE_FORMATS, true);
    minDate = parsed.isValid() ? parsed : undefined;
  } else if (future_only) {
    // No explicit date — block everything before the start of today
    minDate = dayjs().startOf("day");
  }

  let maxDate;
  if (max_date_enabled && max_date) {
    // Explicit max date provided — parse using any valid format strictly
    const parsed = dayjs(max_date, VALID_DATE_FORMATS, true);
    maxDate = parsed.isValid() ? parsed : undefined;
  } else if (past_only) {
    // No explicit date — block everything after the end of today
    maxDate = dayjs().endOf("day");
  }

  return { minDate, maxDate };
};

const preferServerValue = (serverValue, localValue) =>
  serverValue !== undefined ? serverValue : localValue;

const mergeFieldPropertyForPreview = (
  serverProperty = {},
  localProperty = {},
) => ({
  ...serverProperty,
  ...localProperty,
  options: preferServerValue(serverProperty?.options, localProperty?.options),
  conditions: preferServerValue(
    serverProperty?.conditions,
    localProperty?.conditions,
  ),
  option_conditions: preferServerValue(
    serverProperty?.option_conditions,
    localProperty?.option_conditions,
  ),
  input_dropdown: {
    ...serverProperty?.input_dropdown,
    ...localProperty?.input_dropdown,
  },

  input_radio: {
    ...serverProperty?.input_radio,
    ...localProperty?.input_radio,
  },

  input_checkbox: {
    ...serverProperty?.input_checkbox,
    ...localProperty?.input_checkbox,
  },
  validations: preferServerValue(
    serverProperty?.validations,
    localProperty?.validations,
  ),
  linked_field: preferServerValue(
    serverProperty?.linked_field,
    localProperty?.linked_field,
  ),
  input_calculation_variables: preferServerValue(
    serverProperty?.input_calculation_variables,
    localProperty?.input_calculation_variables,
  ),
  input_current_time: {
    ...serverProperty?.input_current_time,
    ...localProperty?.input_current_time,
    dependency: preferServerValue(
      serverProperty?.input_current_time?.dependency,
      localProperty?.input_current_time?.dependency,
    ),
    time_greater_than: preferServerValue(
      serverProperty?.input_current_time?.time_greater_than,
      localProperty?.input_current_time?.time_greater_than,
    ),
    time_less_than: preferServerValue(
      serverProperty?.input_current_time?.time_less_than,
      localProperty?.input_current_time?.time_less_than,
    ),
  },
  input_date_and_time: {
    ...serverProperty?.input_date_and_time,
    ...localProperty?.input_date_and_time,
    dependency: preferServerValue(
      serverProperty?.input_date_and_time?.dependency,
      localProperty?.input_date_and_time?.dependency,
    ),
  },
  input_date: {
    ...serverProperty?.input_date,
    ...localProperty?.input_date,
    dependency: preferServerValue(
      serverProperty?.input_date?.dependency,
      localProperty?.input_date?.dependency,
    ),
    min_date_enabled: preferServerValue(
      serverProperty?.input_date?.min_date_enabled,
      localProperty?.input_date?.min_date_enabled,
    ),
    max_date_enabled: preferServerValue(
      serverProperty?.input_date?.max_date_enabled,
      localProperty?.input_date?.max_date_enabled,
    ),
    min_date: preferServerValue(
      serverProperty?.input_date?.min_date,
      localProperty?.input_date?.min_date,
    ),
    max_date: preferServerValue(
      serverProperty?.input_date?.max_date,
      localProperty?.input_date?.max_date,
    ),
    past_only: preferServerValue(
      serverProperty?.input_date?.past_only,
      localProperty?.input_date?.past_only,
    ),
    future_only: preferServerValue(
      serverProperty?.input_date?.future_only,
      localProperty?.input_date?.future_only,
    ),
  },
});

const mergeSectionsForPreview = (serverSections = [], localSections = []) => {
  const localSectionsMap = new Map(
    (localSections || []).map((section) => [section?.section_uuid, section]),
  );

  const mergedSections = (serverSections || []).map(
    (serverSection, sectionIndex) => {
      const localSection = localSectionsMap.get(serverSection?.section_uuid);
      if (!localSection) {
        return serverSection;
      }

      const serverFields = serverSection?.fields || [];
      const localFieldsMap = new Map(
        (localSection?.fields || []).map((field) => [field?.input_uuid, field]),
      );

      const mergedFields = serverFields.map((serverField, fieldIndex) => {
        const localField = localFieldsMap.get(serverField?.input_uuid);
        if (!localField) {
          return serverField;
        }

        return {
          ...serverField,
          label: localField?.label ?? serverField?.label,
          placeholder: localField?.placeholder ?? serverField?.placeholder,
          order: localField?.order ?? serverField?.order ?? fieldIndex + 1,
          value: localField?.value ?? serverField?.value,
          property: mergeFieldPropertyForPreview(
            serverField?.property,
            localField?.property,
          ),
        };
      });

      const serverFieldIds = new Set(
        serverFields.map((field) => field?.input_uuid),
      );
      const localOnlyFields = (localSection?.fields || []).filter(
        (field) => !serverFieldIds.has(field?.input_uuid),
      );

      return {
        ...serverSection,
        name: localSection?.name ?? serverSection?.name,
        description: localSection?.description ?? serverSection?.description,
        order: localSection?.order ?? serverSection?.order ?? sectionIndex + 1,
        property: {
          ...serverSection?.property,
          ...localSection?.property,
        },
        fields: [...mergedFields, ...localOnlyFields],
      };
    },
  );

  const serverSectionIds = new Set(
    (serverSections || []).map((section) => section?.section_uuid),
  );
  const localOnlySections = (localSections || []).filter(
    (section) => !serverSectionIds.has(section?.section_uuid),
  );

  return [...mergedSections, ...localOnlySections];
};
const modules = {
  toolbar: {
    container: "#quill-toolbar",
  },
  clipboard: { matchVisual: false },
};

const formats = [
  "header",
  "bold",
  "italic",
  "underline",
  "strike",
  "color",
  "align",
  "list",
  "bullet",
  "link",
  "image",
];
// ---------------------------------------------------------------------------
// DynamicFormGenerator
// ---------------------------------------------------------------------------
const getDependencyValues = (dependencies = [], allFields = []) => {
  return dependencies.map((dep) => {
    const matchedField = allFields.find((f) => f.input_uuid === dep.field_uuid);

    return {
      query_param: dep.query_param,
      value: matchedField?.value,
    };
  });
};

const getFieldDependencies = (field, allFields = []) => {
  const cfg =
    field?.property?.input_dropdown ||
    field?.property?.input_radio ||
    field?.property?.input_checkbox;

  // PRIORITY 1 -> master_dependencies
  if (cfg?.master_dependencies?.length) {
    return getDependencyValues(cfg.master_dependencies, allFields);
  }

  // PRIORITY 2 -> depends_on
  if (cfg?.depends_on) {
    const matchedField = allFields.find((f) => f.input_uuid === cfg.depends_on);

    return [
      {
        query_param: null,
        value: matchedField?.value,
      },
    ];
  }

  return [];
};

const hasMeaningfulValue = (value) =>
  value !== undefined && value !== null && value !== "";

const getEmptyFieldValue = (field = {}) => {
  switch (field?.property?.field_type) {
    case "Toggle":
      return false;
    case "Input Checkbox":
      return [];
    case "File Upload":
    case "Image Upload":
      return null;
    default:
      return "";
  }
};

const getInitialFieldValue = (field = {}) => {
  if (hasMeaningfulValue(field?.value) || field?.value === false) {
    return field.value;
  }

  switch (field?.property?.field_type) {
    case "Input Text":
      return field?.property?.input_text?.default_val ?? "";
    case "Input Number":
      return field?.property?.input_number?.default_val ?? "";
    case "Input Email":
      return field?.property?.input_text?.default_val ?? "";
    case "Input Current Time":
      return field?.property?.input_current_time?.default_time ?? "";
    case "Input Date":
      return field?.property?.input_date?.default_date ?? "";
    case "Input Date and Time":
      return field?.property?.input_date_and_time?.default_datetime ?? "";
    case "Toggle":
      return false;
    case "Input Checkbox":
      return [];
    case "Linked Field":
      return field?.property?.linked_field?.default_val ?? "";
    case "Auto Increment":
      return field?.property?.auto_increment?.default_val ?? "";
    case "File Upload":
    case "Image Upload":
      return null;
    default:
      return "";
  }
};

const buildFlatDefaultValues = (sections = []) => {
  const defaults = {};

  (sections || []).forEach((section) => {
    (section?.fields || []).forEach((field) => {
      defaults[field.input_uuid] = getInitialFieldValue(field);
    });
  });

  return defaults;
};

const resolveFieldOptions = (
  field,
  liveOptionsState = {},
  dynamicOptions = {},
) => {
  const liveOptions = (
    dynamicOptions?.[field.input_uuid] || liveOptionsState?.[field.input_uuid]
  )?.options;
  const schemaOptions = field?.property?.options?.options || [];
  return Array.isArray(liveOptions) && liveOptions.length
    ? liveOptions
    : schemaOptions;
};

const getHiddenFieldUuids = (
  sections = [],
  values = {},
  liveOptionsState = {},
  dynamicOptions = {},
) => {
  const hidden = new Set();
  if (!sections?.length) return hidden;

  const allFields = sections.flatMap((section) => section.fields || []);
  allFields
    .filter((fieldItem) => fieldItem.property?.conditions?.length)
    .forEach((fieldItem) => {
      const selectedValue = values?.[fieldItem.input_uuid] ?? fieldItem.value;
      const allOpts = resolveFieldOptions(
        fieldItem,
        liveOptionsState,
        dynamicOptions,
      );

      const selectedValues = Array.isArray(selectedValue)
        ? selectedValue
        : [selectedValue];

      const selectedOptionUuids = allOpts
        .filter((opt) =>
          selectedValues.includes(
            opt.value !== undefined ? opt.value : opt.option_uuid,
          ),
        )
        .map((opt) => opt.option_uuid);

      const conditionsByTarget = {};
      fieldItem.property.conditions.forEach((cond) => {
        const targets = Array.isArray(cond.target_input_field_uuid)
          ? cond.target_input_field_uuid
          : cond.target_input_field_uuid
          ? [cond.target_input_field_uuid]
          : [];

        targets.forEach((uuid) => {
          if (!conditionsByTarget[uuid]) conditionsByTarget[uuid] = [];
          conditionsByTarget[uuid].push(cond.fbt_option_uuid);
        });
      });

      Object.entries(conditionsByTarget).forEach(
        ([targetUuid, optionUuids]) => {
          const anyMatch = selectedOptionUuids.some((uuid) =>
            optionUuids.includes(uuid),
          );
          if (!anyMatch) hidden.add(targetUuid);
        },
      );
    });

  return hidden;
};

const createValidationResolver = (
  sections = [],
  liveOptionsState = {},
  dynamicOptions = {},
) => {
  const schema = generateValidationSchema(sections);

  return async (values = {}) => {
    const hiddenFieldUuids = getHiddenFieldUuids(
      sections,
      values,
      liveOptionsState,
      dynamicOptions,
    );

    const sanitizedValues = { ...values };
    hiddenFieldUuids.forEach((fieldUuid) => {
      delete sanitizedValues[fieldUuid];
    });

    try {
      await schema.validate(sanitizedValues, { abortEarly: false });
      return {
        values,
        errors: {},
      };
    } catch (error) {
      const formattedErrors = {};

      (error?.inner || []).forEach((err) => {
        if (err?.path && !formattedErrors[err.path]) {
          formattedErrors[err.path] = {
            type: err.type || "validation",
            message: err.message,
          };
        }
      });

      if (error?.path && !formattedErrors[error.path]) {
        formattedErrors[error.path] = {
          type: error.type || "validation",
          message: error.message,
        };
      }

      return {
        values: {},
        errors: formattedErrors,
      };
    }
  };
};

const DynamicFormGenerator = ({
  formJson,
  onSubmit,
  loading,
  formData,
  setDFormData,
  dynamicOptions = {},
  fieldLoading = {},
  setFormMethods,
}) => {
  const liveOptionsState = useSelector(
    (state) => state.bmrFbProperty?.options || {},
  );
  const quillRef = useRef({});
  const formMethodsRef = useRef(null);
  const [values, setValues] = useState({});

  const formSections = useMemo(
    () =>
      formJson.map((section) => ({
        ...section,
        fieldGroups: buildFieldGroups(section.fields),
      })),
    [formJson],
  );

  const defaultValues = useMemo(
    () => buildFlatDefaultValues(formJson),
    [formJson],
  );

  const resolverContextRef = useRef({
    sections: formJson,
    liveOptionsState,
    dynamicOptions,
  });

  useEffect(() => {
    resolverContextRef.current = {
      sections: formJson,
      liveOptionsState,
      dynamicOptions,
    };
  }, [formJson, liveOptionsState, dynamicOptions]);

  const validationResolver = useMemo(
    () =>
      async (values = {}) => {
        const {
          sections,
          liveOptionsState: currentLiveOptionsState,
          dynamicOptions: currentDynamicOptions,
        } = resolverContextRef.current;
        return createValidationResolver(
          sections,
          currentLiveOptionsState,
          currentDynamicOptions,
        )(values);
      },
    [],
  );

  /** Options keyed by input_uuid — for radio / select / checkbox fields. */
  const applyOptionConditions = (formJson, formData, baseOptions) => {
    const updatedOptions = { ...baseOptions };

    if (!formData?.sections?.length) return updatedOptions;

    const allFields = formData.sections.flatMap((s) => s.fields || []);

    allFields.forEach((field) => {
      const selectedValue = field.value;

      const allOpts = field?.property?.options?.options || [];

      const selectedOption = allOpts.find(
        (opt) => (opt.value ?? opt.option_uuid) === selectedValue,
      );

      if (!selectedOption) return;

      const selectedOptionUuid = selectedOption.option_uuid;

      const optionConditions = field?.property?.option_conditions || [];

      optionConditions.forEach((cond) => {
        if (cond.fbt_option_uuid !== selectedOptionUuid) return;

        cond.targets?.forEach((target) => {
          const targetFieldUuid = target.target_field_uuid;
          const allowedOptions = target?.filter_values?.allowed_options || [];

          if (updatedOptions[targetFieldUuid]) {
            updatedOptions[targetFieldUuid] = updatedOptions[
              targetFieldUuid
            ].filter((opt) => allowedOptions.includes(opt.option_uuid));
          }
        });
      });
    });

    return updatedOptions;
  };

  const optionsByField = useMemo(() => {
    const map = {};
    formJson.forEach((section) =>
      section.fields.forEach((field) => {
        const liveOptions = (
          dynamicOptions?.[field.input_uuid] ||
          liveOptionsState?.[field.input_uuid]
        )?.options;
        const schemaOptions = field?.property?.options?.options || [];
        const opts =
          Array.isArray(liveOptions) && liveOptions.length
            ? liveOptions
            : schemaOptions;
        map[field.input_uuid] = opts.map((opt) => ({
          value: opt.value !== undefined ? opt.value : opt.option_uuid,
          label: opt.label,
          option_uuid: opt.option_uuid || opt.value,
        }));
      }),
    );
    // return map;
    return applyOptionConditions(formJson, formData, map);
  }, [formJson, liveOptionsState, dynamicOptions, formData]);

  /** Flat option-uuid → label dictionary for Linked Fields. */
  const allOptionsDict = useMemo(() => {
    const dict = {};
    formJson.forEach((section) =>
      section.fields.forEach((field) => {
        const liveOptions = liveOptionsState?.[field.input_uuid]?.options;
        const schemaOptions = field?.property?.options?.options || [];
        const opts =
          Array.isArray(liveOptions) && liveOptions.length
            ? liveOptions
            : schemaOptions;
        opts.forEach((opt) => {
          dict[opt.option_uuid] = opt.label;
        });
      }),
    );
    return dict;
  }, [formJson, liveOptionsState]);

  // ── Conditional visibility ─────────────────────────────────────────────────
  const hiddenFields = useMemo(() => {
    if (!formData?.sections?.length) return [];

    const valuesByFieldUuid = {};
    formData.sections.forEach((section) => {
      (section.fields || []).forEach((field) => {
        valuesByFieldUuid[field.input_uuid] = field.value;
      });
    });

    return Array.from(
      getHiddenFieldUuids(
        formData.sections,
        valuesByFieldUuid,
        liveOptionsState,
        dynamicOptions,
      ),
    );
  }, [formData, liveOptionsState, dynamicOptions]);

  // ── Field value updater ────────────────────────────────────────────────────
  const handleFieldChange = (fieldUuid, value) => {
    formMethodsRef.current?.setValue(fieldUuid, value, {
      shouldDirty: true,
      shouldTouch: true,
      shouldValidate: true,
    });

    const dependentFieldList = [];
    formData.sections.forEach((section) => {
      (section.fields || []).forEach((field) => {
        const cfg =
          field?.property?.input_dropdown ||
          field?.property?.input_radio ||
          field?.property?.input_checkbox;
        if (!cfg?.is_dependent) return;

        const deps = cfg?.master_dependencies?.length
          ? cfg.master_dependencies.map((dependency) => dependency.field_uuid)
          : cfg?.depends_on
          ? [cfg.depends_on]
          : [];

        if (deps.includes(fieldUuid)) {
          dependentFieldList.push(field);
        }
      });
    });

    dependentFieldList.forEach((field) => {
      formMethodsRef.current?.setValue(
        field.input_uuid,
        getEmptyFieldValue(field),
        {
          shouldDirty: true,
          shouldTouch: true,
          shouldValidate: true,
        },
      );
    });

    if (dependentFieldList.length) {
      formMethodsRef.current?.trigger(
        dependentFieldList.map((field) => field.input_uuid),
      );
    }

    setDFormData((prev) => {
      const allFields = prev.sections.flatMap((s) => s.fields || []);

      // Collect UUIDs of dependent fields that depend on the changed field,
      // so their selected value can be cleared when the parent changes.
      const dependentFieldUuids = new Set();
      allFields.forEach((f) => {
        const cfg =
          f?.property?.input_dropdown ||
          f?.property?.input_radio ||
          f?.property?.input_checkbox;
        if (!cfg?.is_dependent) return;

        const deps = cfg?.master_dependencies?.length
          ? cfg.master_dependencies.map((d) => d.field_uuid)
          : cfg?.depends_on
          ? [cfg.depends_on]
          : [];

        if (deps.includes(fieldUuid)) {
          dependentFieldUuids.add(f.input_uuid);
        }
      });

      return {
        ...prev,
        sections: prev.sections.map((section) => ({
          ...section,
          fields: section.fields.map((field) => {
            if (field.input_uuid === fieldUuid) return { ...field, value };
            // Clear value of any dependent field whose parent just changed
            if (dependentFieldUuids.has(field.input_uuid)) {
              return { ...field, value: getEmptyFieldValue(field) };
            }
            return field;
          }),
        })),
      };
    });
  };

  const handleQuillChange = (val, id) => {
    setValues((prev) => ({ ...prev, [id]: val }));
    handleFieldChange(id, val);
  };

  const normalizeDateFormat = (format = "") => {
    if (!format) return "DD/MM/YY";
    return format.replace(/:/g, "/").replace(/-/g, "/").replace(/\s+/g, "");
  };
  // ── Render ─────────────────────────────────────────────────────────────────
  return (
    <FormWrapper
      onSubmit={onSubmit}
      loading={loading}
      defaultValues={defaultValues}
      resolver={validationResolver}
      mode="onTouched"
      reValidateMode="onChange"
      renderForm={false}
    >
      {(formMethods) => {
        formMethodsRef.current = formMethods;

        if (setFormMethods) {
          setFormMethods(formMethods);
        }
        return (
          <>
            {/* ── Single outer border containing all sections ── */}
            <Box
              sx={{
                // border: "1px solid #E0E0E0",
                borderRadius: "8px",
                overflow: "hidden",
              }}
            >
              {formSections.map((section, index) => (
                <Box key={section?.section_uuid}>
                  {/* ── Section header ── */}
                  {(section?.property?.display_details ||
                    section?.property?.display_field_number) && (
                    <Box
                      sx={{
                        minHeight: "56px",
                        display: "flex",
                        alignItems: "center",
                        px: 2,
                        py: 1,
                      }}
                    >
                      <Box>
                        {section?.property?.display_details &&
                        section?.property?.display_field_number ? (
                          <>
                            <Typography variant="h6" sx={{ fontWeight: 600 }}>
                              {`${index + 1}. ${section?.name}`}
                            </Typography>
                            {section?.description && (
                              <Typography
                                variant="body2"
                                sx={{
                                  fontWeight: 400,
                                  color: "text.secondary",
                                }}
                              >
                                {section?.description}
                              </Typography>
                            )}
                          </>
                        ) : section?.property?.display_field_number ? (
                          <Typography variant="h6" sx={{ fontWeight: 600 }}>
                            {`${index + 1}.`}
                          </Typography>
                        ) : section?.property?.display_details ? (
                          <>
                            <Typography
                              variant="h6"
                              sx={{
                                fontWeight: 600,
                                textTransform: "uppercase",
                              }}
                            >
                              {section?.name}
                            </Typography>
                            {section?.description && (
                              <Typography
                                variant="body2"
                                sx={{
                                  fontWeight: 400,
                                  fontSize: "0.875rem",
                                  color: "text.secondary",
                                  textTransform: "capitalize",
                                }}
                              >
                                {section?.description}
                              </Typography>
                            )}
                          </>
                        ) : null}
                      </Box>
                    </Box>
                  )}

                  {/* ── Section fields ── */}
                  <Box
                    className="p-3 px-2"
                    sx={{
                      ...((section?.property?.type === "card" ||
                        section?.property?.type === "group") && {
                        border: "1px solid #E0E0E0",
                        borderRadius: "8px",
                        backgroundColor: "#fff",
                        boxShadow: "0px 2px 4px rgba(0,0,0,0.05)",
                        m: 1,
                        p: 1,
                      }),
                    }}
                  >
                    {section.fieldGroups.map((group, groupIndex) => (
                      <Grid key={groupIndex} container spacing={2}>
                        {group.fields.map((field) => {
                          // if (hiddenFields.includes(field.input_uuid)) return null;
                          const cfg =
                            field?.property?.input_dropdown ||
                            field?.property?.input_radio ||
                            field?.property?.input_checkbox;

                          const allFields = formData.sections.flatMap(
                            (s) => s.fields || [],
                          );

                          const dependencyValues = getFieldDependencies(
                            field,
                            allFields,
                          );

                          const hasDependencyValue =
                            dependencyValues.length > 0 &&
                            dependencyValues.every((dep) => dep.value);

                          // ── FIELD VISIBILITY LOGIC ─────────────────────────────
                          const isHidden =
                            cfg?.is_dependent &&
                            cfg?.is_visible === true &&
                            !hasDependencyValue;

                          // If hidden, return null immediately to prevent rendering or API side-effects
                          if (isHidden) return null;

                          // existing conditional visibility
                          if (hiddenFields.includes(field.input_uuid))
                            return null;
                          const datetimeFormat = normalizeDateFormat(
                            field?.property?.input_date_and_time
                              ?.date_display_format,
                          );
                          const timedateFormat =
                            field?.property?.input_date_and_time
                              ?.time_format === "24_hour"
                              ? "HH:mm"
                              : "hh:mm A";

                          // Change to use the actual format provided by the API:
                          const apiTimeFormat =
                            field?.property?.input_date_and_time?.time_format ||
                            "hh:mm A";
                          const dateTimeFormat = `${datetimeFormat} ${apiTimeFormat}`;
                          // const dateTimeFormat = `${datetimeFormat} ${timedateFormat}`;

                          const rawMin =
                            field?.property?.input_date_and_time?.min_datetime;
                          const rawMax =
                            field?.property?.input_date_and_time?.max_datetime;

                          const parsedMin = rawMin
                            ? dayjs(rawMin, dateTimeFormat, true)
                            : null;

                          const parsedMax = rawMax
                            ? dayjs(rawMax, dateTimeFormat)
                            : null;
                          // Live value from formData, falling back to static field.value
                          const fieldValue =
                            formData.sections
                              .flatMap((s) => s.fields)
                              .find((f) => f.input_uuid === field.input_uuid)
                              ?.value ??
                            field.value ??
                            "";

                          const showFormula = field?.property?.calculation
                            ?.display_formula
                            ? JSON.parse(
                                field.property.calculation.display_formula,
                              )
                            : false;
                          const calcProp = field?.property?.calculation;

                          // ── Derive date props from API shape ───────────────────
                          const inputDate = field?.property?.input_date || {};

                          // date_display_format: validated against known formats
                          const dateFormat = VALID_DATE_FORMATS.includes(
                            inputDate.date_display_format,
                          )
                            ? inputDate.date_display_format
                            : "DD/MM/YY";

                          // date_range: true → range picker
                          const isRange = inputDate.date_range === true;

                          // minDate / maxDate as dayjs objects (or undefined)
                          const { minDate, maxDate } =
                            resolveDateConstraints(inputDate);

                          // ── onChange handler ───────────────────────────────────
                          const handleChange = (input) => {
                            let finalValue;

                            switch (field.property.field_type) {
                              case "Input Date":
                                if (isRange) {
                                  finalValue =
                                    input && typeof input === "object"
                                      ? {
                                          start: input.start
                                            ? dayjs(
                                                input.start,
                                                dateFormat,
                                                true,
                                              ).format("YYYY-MM-DD")
                                            : null,
                                          end: input.end
                                            ? dayjs(
                                                input.end,
                                                dateFormat,
                                                true,
                                              ).format("YYYY-MM-DD")
                                            : null,
                                        }
                                      : { start: null, end: null };
                                } else {
                                  finalValue =
                                    input && typeof input === "string"
                                      ? dayjs(input, dateFormat, true).format(
                                          "YYYY-MM-DD",
                                        )
                                      : "";
                                }
                                break;

                              case "Input Current Time":
                                finalValue = input || "";
                                break;

                              case "Input Date and Time": {
                                // Use the same format logic defined for your DateTimePicker
                                const datetimeFormat = normalizeDateFormat(
                                  field?.property?.input_date_and_time
                                    ?.date_display_format,
                                );
                                const timedateFormat =
                                  field?.property?.input_date_and_time
                                    ?.time_format === "24_hour"
                                    ? "HH:mm"
                                    : "hh:mm A";
                                const format = `${datetimeFormat} ${timedateFormat}`;

                                // If input is a dayjs object, format it. Otherwise keep as string or empty.
                                finalValue =
                                  input &&
                                  dayjs.isDayjs(input) &&
                                  input.isValid()
                                    ? input.format(format)
                                    : typeof input === "string"
                                    ? input
                                    : "";
                                break;
                              }
                              default:
                                if (
                                  typeof input === "object" &&
                                  input !== null &&
                                  input.$d
                                ) {
                                  finalValue = input;
                                } else if (input?.target) {
                                  finalValue = input.target.value;
                                } else {
                                  finalValue = input;
                                }
                                break;
                            }

                            // AFTER
                            handleFieldChange(field.input_uuid, finalValue);
                          };

                          return (
                            <Grid
                              key={field.input_uuid}
                              item
                              xs={12}
                              sm={LAYOUT_OPTIONS[group.layout]?.gridSize || 12}
                            >
                              <Box sx={{ paddingTop: "1rem", padding: 0 }}>
                                {/* ── Field label heading ── */}
                                {field.type === "label" ? (
                                  <Typography
                                    variant="body"
                                    className="text-sm font-bold"
                                  >
                                    {field?.label}
                                  </Typography>
                                ) : (
                                  <Box
                                    sx={{
                                      minHeight: "1.5rem",
                                      pb: "0.625rem",
                                      visibility: field?.property?.general
                                        ?.remove_heading
                                        ? "hidden"
                                        : "visible",
                                    }}
                                  >
                                    <Typography
                                      variant="subtitle1"
                                      className="inline-block font-normal"
                                      sx={{ paddingBottom: "0.625rem" }}
                                    >
                                      <Box
                                        component="span"
                                        sx={{ textTransform: "capitalize" }}
                                      >
                                        {field?.label}{" "}
                                      </Box>
                                      {/* {calcProp?.unit_of_measurement
                                ? `(${calcProp.unit_of_measurement})`
                                : field?.property?.input_number
                                  ?.measurement_unit
                                  ? `(${field.property.input_number.measurement_unit})`
                                  : ""} */}
                                      {field?.property?.general?.required && (
                                        <Box
                                          component="span"
                                          className="text-red-500"
                                          sx={{ ml: 0.5 }}
                                        >
                                          *
                                        </Box>
                                      )}
                                    </Typography>
                                  </Box>
                                )}
                                {/* ── Field input ── */}
                                {(() => {
                                  let customRules = {};
                                  if (
                                    field.property.field_type ===
                                    "Input Current Time"
                                  ) {
                                    const timeProps =
                                      field.property.input_current_time || {};
                                    const dependency =
                                      timeProps.dependency || {};

                                    customRules.validate = (value) => {
                                      if (!value) return true;
                                      const timeFmt =
                                        timeProps.time_format === "24_hour"
                                          ? "HH:mm"
                                          : "hh:mm A";
                                      const current = dayjs(
                                        value,
                                        timeFmt,
                                        true,
                                      );
                                      if (!current.isValid()) return true;

                                      if (
                                        dependency &&
                                        dependency.dependency_uuid
                                      ) {
                                        const otherRef = formData.sections
                                          .flatMap((s) => s.fields)
                                          .find(
                                            (f) =>
                                              f.input_uuid ===
                                              dependency.dependency_uuid,
                                          );
                                        if (otherRef && otherRef.value) {
                                          const other = dayjs(
                                            otherRef.value,
                                            timeFmt,
                                            true,
                                          );
                                          if (other.isValid()) {
                                            if (
                                              dependency.type === "greater" ||
                                              dependency.type === "greater_than"
                                            ) {
                                              if (!current.isAfter(other)) {
                                                return `Time does not meet conditions: must be greater than ${
                                                  dependency.dependency_label ||
                                                  otherRef.label ||
                                                  "the dependent field"
                                                }`;
                                              }
                                            } else if (
                                              dependency.type === "less" ||
                                              dependency.type === "less_than" ||
                                              dependency.type === "lessthan"
                                            ) {
                                              if (!current.isBefore(other)) {
                                                return `Time does not meet conditions: must be less than ${
                                                  dependency.dependency_label ||
                                                  otherRef.label ||
                                                  "the dependent field"
                                                }`;
                                              }
                                            }
                                          }
                                        }
                                      }
                                      return true;
                                    };
                                  }

                                  if (
                                    field.property.field_type === "Calculation"
                                  ) {
                                    return (
                                      <>
                                        <FormField
                                          name={field.input_uuid}
                                          type="number"
                                          label=""
                                          fullWidth
                                          disabled
                                          // size="small"
                                          endAdornment={
                                            <InputAdornment
                                              position="end"
                                              sx={{
                                                display: "flex",
                                                alignItems: "center",
                                                mr: 1.5,
                                              }}
                                            >
                                              <Typography
                                                variant="body2"
                                                sx={{
                                                  color: "#6b7280",
                                                  fontSize: "0.8rem",
                                                  fontWeight: 500,
                                                  whiteSpace: "nowrap",
                                                }}
                                              >
                                                {calcProp?.unit_of_measurement ||
                                                  ""}
                                              </Typography>
                                            </InputAdornment>
                                          }
                                        />
                                        {showFormula && calcProp?.formula && (
                                          <Typography variant="caption">
                                            Formula: {field?.parsedFormula}
                                          </Typography>
                                        )}
                                      </>
                                    );
                                  } else if (
                                    field.property.field_type === "Linked Field"
                                  ) {
                                    return (
                                      <FormField
                                        name={field.input_uuid}
                                        label=""
                                        fullWidth
                                        disabled
                                        size="small"
                                      />
                                    );
                                  } else if (
                                    field.property.field_type ===
                                    "Rich Text Editor"
                                  ) {
                                    return (
                                      <>
                                        <div className="border-2 rounded-2xl disclaimer-quill-wrapper">
                                          <ReactQuill
                                            key={field.input_uuid}
                                            value={
                                              values[field.input_uuid] || ""
                                            }
                                            onChange={(val) =>
                                              handleQuillChange(
                                                val,
                                                field.input_uuid,
                                              )
                                            }
                                            theme="snow"
                                            modules={{
                                              toolbar: [
                                                [{ header: [1, 2, false] }],
                                                [
                                                  "bold",
                                                  "italic",
                                                  "underline",
                                                  "strike",
                                                ],
                                                [{ color: [] }],
                                                [{ align: [] }],
                                                [
                                                  { list: "ordered" },
                                                  { list: "bullet" },
                                                ],
                                                ["link", "image"],
                                              ],
                                            }}
                                            formats={formats}
                                          />
                                        </div>
                                      </>
                                    );
                                  } else {
                                    const isLoading =
                                      fieldLoading[field.input_uuid];

                                    if (
                                      [
                                        "radio-group",
                                        "select",
                                        "checkbox-group",
                                      ].includes(field.type) &&
                                      isLoading
                                    ) {
                                      return (
                                        <Skeleton
                                          variant="rectangular"
                                          height={40}
                                          sx={{ borderRadius: "6px" }}
                                        />
                                      );
                                    }

                                    return (
                                      <FormField
                                        rules={customRules}
                                        name={field.input_uuid}
                                        type={field.type}
                                        options={(() => {
                                          if (
                                            ![
                                              "radio-group",
                                              "select",
                                              "checkbox-group",
                                            ].includes(field.type)
                                          )
                                            return [];

                                          const cfg =
                                            field?.property?.input_dropdown ||
                                            field?.property?.input_radio ||
                                            field?.property?.input_checkbox;

                                          const allFields =
                                            formData.sections.flatMap(
                                              (s) => s.fields || [],
                                            );

                                          const dependencyValues =
                                            getFieldDependencies(
                                              field,
                                              allFields,
                                            );

                                          const hasDependencyValue =
                                            dependencyValues.every(
                                              (dep) => dep.value,
                                            );

                                          // DATA VISIBILITY LOGIC

                                          // CASE 1:
                                          // preload/show data immediately
                                          if (cfg?.is_data_visible === true) {
                                            return (
                                              optionsByField[
                                                field.input_uuid
                                              ] || []
                                            );
                                          }

                                          // CASE 2:
                                          // field visible only after dependency selected
                                          if (cfg?.is_visible === true) {
                                            return hasDependencyValue
                                              ? optionsByField[
                                                  field.input_uuid
                                                ] || []
                                              : [];
                                          }
                                          // CASE 3:
                                          // preload data immediately
                                          return (
                                            optionsByField[field.input_uuid] ||
                                            []
                                          );
                                        })()}
                                        row={
                                          field.type === "radio-group"
                                            ? !!field?.property?.input_radio
                                                ?.is_horizontal
                                            : field.type === "checkbox-group"
                                            ? !!field?.property?.input_checkbox
                                                ?.alignment_horizontal
                                            : false
                                        }
                                        nomargin
                                        placeholder={
                                          field.placeholder ||
                                          `Enter ${
                                            field.label?.toLowerCase() ||
                                            "value"
                                          }`
                                        }
                                        tooltip={field.placeholder}
                                        // hasbothlabels={field.type === "Toggle"}
                                        label1={
                                          field?.property?.toggle?.toggle_msg_1
                                        }
                                        label2={
                                          field?.property?.toggle?.toggle_msg_2
                                        }
                                        disabled={
                                          field?.property?.general?.disable
                                        }
                                        endAdornment={(() => {
                                          const unit =
                                            field.property.field_type ===
                                            "Calculation"
                                              ? field?.property?.calculation
                                                  ?.unit_of_measurement
                                              : field.type === "number"
                                              ? field?.property?.input_number
                                                  ?.measurement_unit
                                              : field.type === "text"
                                              ? field?.property?.input_text
                                                  ?.measurement_unit_text
                                              : "";

                                          if (!unit) return null;

                                          return (
                                            <InputAdornment
                                              position="end"
                                              sx={{
                                                display: "flex",
                                                alignItems: "center",
                                                mr: 1.5,
                                              }}
                                            >
                                              <Typography
                                                variant="body2"
                                                sx={{
                                                  color: "#6b7280",
                                                  fontSize: "0.8rem",
                                                  fontWeight: 500,
                                                  whiteSpace: "nowrap",
                                                }}
                                              >
                                                {unit}
                                              </Typography>
                                            </InputAdornment>
                                          );
                                        })()}
                                        onChange={handleChange}
                                        // ── Date props ────────────────────────────────────
                                        dateFormat={dateFormat}
                                        dateRange={isRange}
                                        minDate={minDate}
                                        maxDate={maxDate}
                                        // ── DateTime props ────────────────────────────────
                                        combineDate={
                                          field.property.field_type ===
                                          "Input Date and Time"
                                            ? field?.property
                                                ?.input_date_and_time
                                                ?.date_display_format
                                            : "DD/MM/YY"
                                        }
                                        combineTime={
                                          field.property.field_type ===
                                          "Input Date and Time"
                                            ? field?.property
                                                ?.input_date_and_time
                                                ?.time_format
                                            : "HH:mm"
                                        }
                                        // ── Time props ────────────────────────────────────
                                        timeFormat={
                                          field.property.field_type ===
                                          "Input Current Time"
                                            ? field?.property
                                                ?.input_current_time
                                                ?.time_format
                                            : "hh:mm A"
                                        }
                                        currentTime={
                                          field.property.field_type ===
                                          "Input Current Time"
                                            ? field?.property
                                                ?.input_current_time
                                                ?.current_time
                                            : false
                                        }
                                        defaultTime={
                                          field.property.field_type ===
                                          "Input Current Time"
                                            ? field?.property
                                                ?.input_current_time
                                                ?.default_time
                                            : ""
                                        }
                                        // ── Other ─────────────────────────────────────────
                                        linkLabel={
                                          field?.property?.hyperlink?.name
                                        }
                                        linkUrl={
                                          field?.property?.hyperlink?.url
                                        }
                                        defaultValue={
                                          field.property.field_type ===
                                          "Input Text"
                                            ? field?.property?.input_text
                                                ?.default_val
                                            : field.property.field_type ===
                                              "Input Number"
                                            ? field?.property?.input_number
                                                ?.default_val ?? null
                                            : field.property.field_type ===
                                              "Auto Increment"
                                            ? field?.property?.auto_increment
                                                ?.default_val
                                            : ""
                                        }
                                        minDateTime={
                                          field?.property?.input_date_and_time
                                            ?.min_datetime_enabled &&
                                          parsedMin?.isValid()
                                            ? parsedMin
                                            : undefined
                                        }
                                        maxDateTime={
                                          field?.property?.input_date_and_time
                                            ?.max_datetime_enabled &&
                                          parsedMax?.isValid()
                                            ? parsedMax
                                            : undefined
                                        }
                                        currentDate={
                                          field?.property?.input_date
                                            ?.current_date
                                        }
                                        defaultDate={
                                          field?.property?.input_date
                                            ?.default_date
                                        }
                                        dateReadonly={
                                          field?.property?.input_date
                                            ?.current_date ||
                                          field?.property?.input_date
                                            ?.default_date
                                        }
                                        currentDateTime={
                                          field?.property?.input_date_and_time
                                            ?.current_datetime
                                        }
                                        defaultDateTime={
                                          field?.property?.input_date_and_time
                                            ?.default_datetime
                                        }
                                        futureOnly={
                                          field?.property?.input_date_and_time
                                            ?.future_only_datetime
                                        }
                                        pastOnly={
                                          field?.property?.input_date_and_time
                                            ?.past_only_datetime
                                        }
                                      />
                                    );
                                  }
                                })()}
                              </Box>

                              {field?.property?.note?.message_note && (
                                <Typography
                                  variant="body"
                                  sx={{ mb: 1, color: "#FB9D2C" }}
                                >
                                  {`Note : ${field.property.note.message_note}`}
                                </Typography>
                              )}
                            </Grid>
                          );
                        })}
                      </Grid>
                    ))}
                    {section?.property?.type === "group" && (
                      <Box display="flex" justifyContent="end" className="pt-2">
                        <Button
                          variant="contained"
                          disabled={section?.property?.type === "group"}
                        >
                          Add
                        </Button>
                      </Box>
                    )}
                  </Box>
                </Box>
              ))}
            </Box>
          </>
        );
      }}
    </FormWrapper>
  );
};

// ---------------------------------------------------------------------------
// ValidationAndSubmit
// ---------------------------------------------------------------------------

const ValidationAndSubmit = ({ handleCloseDrawer, cardId, getValues }) => {
  const { id } = useParams();
  const location = useLocation();
  const [loading, setLoading] = useState(false);
  const apiCacheRef = useRef({});
  const fetchingRef = useRef({});
  const formMethodsRef = useRef(null);
  const [fieldLoading, setFieldLoading] = useState({});
  const FORM_SCHEMA_UPDATED = useSelector(
    (state) => state.bmrFbSections.formData.sections,
  );
  const { fbTemplate } = useSelector((state) => state.bmrFbl);
  const templateName = fbTemplate.items.find(
    (name) => name?.id == (id ?? cardId),
  );

  const dispatch = useDispatch();
  const [formData, setDynamicFormData] = useState({
    sections: FORM_SCHEMA_UPDATED || [],
  });
  const [openConfirmation, setOpenConfirmation] = useState(false);

  const handleCloseConfirmation = () => setOpenConfirmation(false);
  const handleSubmit = () => dispatch(submitSection(id ?? cardId));

  const handlePublish = async () => {
    try {
      const response = await dispatch(pusblishTemplate(id ?? cardId)).unwrap();
      if (!location.pathname.includes("preview")) handleCloseDrawer();
      dispatch(updateTemplateStatus({ id, status: response.data.status }));
      dispatch(
        fetchTemplates({
          page: 1,
          per_page: 12,
          search: null,
          status: "",
          version: "",
        }),
      );
    } catch (error) {
      notify.error(error?.message || error?.data?.message);
    }
  };

  useEffect(() => {
    if (FORM_SCHEMA_UPDATED)
      setDynamicFormData({ sections: FORM_SCHEMA_UPDATED });
  }, [FORM_SCHEMA_UPDATED]);

  const injectDefaultValues = useCallback(
    (sections) =>
      sections.map((section) => ({
        ...section,
        fields: section.fields.map((field) => {
          const hasValue =
            field.value !== undefined &&
            field.value !== null &&
            field.value !== "";
          return {
            ...field,
            value: hasValue
              ? field.value
              : field?.property?.linked_field?.default_val ??
                field?.property?.input_text?.default_val ??
                field?.property?.auto_increment?.default_val ??
                field?.property?.input_number?.default_val ??
                "",
          };
        }),
      })),
    [],
  );

  const fetchSetFormData = useCallback(async () => {
    try {
      setLoading(true);
      const currentFormData =
        typeof getValues === "function" ? getValues("sections") : null;

      if (
        Array.isArray(currentFormData) &&
        currentFormData.length &&
        !cardId &&
        templateName?.status !== 2
      ) {
        await dispatch(
          sectionAutoSave({
            id,
            data: { sections: currentFormData },
          }),
        ).unwrap();
      }

      const response = await dispatch(fetchSections(id ?? cardId)).unwrap();
      const serverSections =
        response.data.sections.length > 0
          ? response.data.sections
          : [SectionSchema];
      const mergedSections =
        Array.isArray(currentFormData) && currentFormData.length
          ? mergeSectionsForPreview(serverSections, currentFormData)
          : serverSections;
      const modifiedSections = injectDefaultValues(mergedSections);
      dispatch(setFormData({ sections: modifiedSections }));
      setDynamicFormData({ sections: modifiedSections });
    } catch (error) {
      console.error("Error:", error);
    } finally {
      setLoading(false);
    }
  }, [cardId, dispatch, getValues, id, injectDefaultValues]);

  useEffect(() => {
    fetchSetFormData();
  }, [fetchSetFormData]);

  const getParsedFormula = (formVariables, formula) => {
    let parsed = formula;
    Object.entries(formVariables).forEach(([key, value]) => {
      parsed = parsed.replace(new RegExp(`\\b${key}\\b`, "g"), value);
    });
    return parsed;
  };

  useEffect(() => {
    if (!formData?.sections?.length) return;

    const inputValues = {};
    const fieldInputLabels = {};
    const updatedFields = [];

    for (const section of formData.sections) {
      for (const field of section.fields || []) {
        const { input_uuid, value, property } = field;
        fieldInputLabels[input_uuid] = field.label;
        inputValues[input_uuid] =
          property?.field_type === "Input Number" && value
            ? parseFloat(value) ?? 0
            : value;
      }
    }

    for (const section of formData.sections) {
      for (const field of section.fields || []) {
        const { input_uuid, value: currentValue, property } = field;
        const {
          field_type,
          calculation,
          input_calculation_variables,
          linked_field,
        } = property || {};

        if (field_type === "Calculation" && calculation?.formula) {
          const variables = {};
          const formVariables = {};
          for (const variable of input_calculation_variables || []) {
            formVariables[variable.name] =
              fieldInputLabels[variable.linked_field_uuid];
            variables[variable.name] =
              inputValues[variable.linked_field_uuid] ||
              (!isNaN(variable.default_val) &&
                parseFloat(variable.default_val)) ||
              0;
          }
          try {
            let result = round(
              evaluate(calculation.formula, variables),
              calculation.decimal_places || 4,
            );
            if (calculation.decimal_places != null) {
              result = parseFloat(
                Number(result).toFixed(calculation.decimal_places),
              );
            }
            const resultStr = result?.toString() ?? "";
            if (currentValue !== resultStr) {
              updatedFields.push({
                fieldUuid: input_uuid,
                value: resultStr,
                parsedFormula: getParsedFormula(
                  formVariables,
                  calculation.formula,
                ),
              });
            }
          } catch (err) {
            console.warn(`Invalid formula for "${input_uuid}": ${err.message}`);
          }
        }

        if (field_type === "Linked Field" && linked_field?.linked_field) {
          const newValue = inputValues[linked_field.linked_field];
          updatedFields.push({
            fieldUuid: input_uuid,
            value:
              newValue !== undefined && newValue !== null && newValue !== ""
                ? newValue
                : linked_field.default_val || "",
          });
        }
      }
    }

    if (!updatedFields.length) return;

    setDynamicFormData((prev) => {
      const updatesMap = Object.fromEntries(
        updatedFields.map(({ fieldUuid, value, parsedFormula }) => [
          fieldUuid,
          { value, parsedFormula },
        ]),
      );
      const newSections = prev.sections.map((section) => ({
        ...section,
        fields: section.fields.map((field) => {
          const update = updatesMap[field.input_uuid];
          if (update && field.value !== update.value) {
            return {
              ...field,
              value: update.value,
              parsedFormula: update.parsedFormula,
            };
          }
          return field;
        }),
      }));
      return JSON.stringify(newSections) === JSON.stringify(prev.sections)
        ? prev
        : { ...prev, sections: newSections };
    });

    updatedFields.forEach(({ fieldUuid, value }) => {
      formMethodsRef.current?.setValue(fieldUuid, value, {
        shouldDirty: true,
        shouldTouch: false,
        shouldValidate: true,
      });
    });
  }, [formData]);

  const [fetchedFields, setFetchedFields] = useState({});
  const [dynamicOptions, setDynamicOptions] = useState({});

  const fetchWithCache = async (endpoint) => {
    if (apiCacheRef.current[endpoint]) {
      return apiCacheRef.current[endpoint];
    }

    const data = await fetchDynamicAPIS(endpoint);
    apiCacheRef.current[endpoint] = data;
    return data;
  };

  useEffect(() => {
    apiCacheRef.current = {};
    fetchingRef.current = {};
  }, [id]);

  // 1. Create a helper to get current dependency values for ALL fields
  const getAllDependencyValues = (formData) => {
    const allFields = formData.sections.flatMap((s) => s.fields || []);
    const deps = {};
    allFields.forEach((field) => {
      // Only track fields that are dependencies for others
      deps[field.input_uuid] = field.value;
    });
    return deps;
  };

  // 2. Inside DynamicPreview component:
  const currentDependencyValues = useMemo(
    () => getAllDependencyValues(formData),
    [formData],
  );

  useEffect(() => {
    if (!formData?.sections?.length) return;

    const processDropdowns = async () => {
      const allFields = formData.sections.flatMap((s) => s.fields || []);
      // const newOptionsMap = { ...dynamicOptions };
      const newOptionsMap = structuredClone(dynamicOptions);
      const newFetchedFields = { ...fetchedFields };
      let hasChanges = false;

      const promises = allFields.map(async (field) => {
        const type = field?.property?.field_type;

        if (
          !["Input Dropdown", "Input Radio", "Input Checkbox"].includes(type)
        ) {
          return null;
        }

        const config =
          field?.property?.input_dropdown ||
          field?.property?.input_radio ||
          field?.property?.input_checkbox;

        if (!config) return null;

        const fieldUuid = field.input_uuid;

        // MASTER FIELD
        if (config.is_master && config.source_endpoint) {
          if (!fetchedFields[fieldUuid]) {
            let endpoint = config.source_endpoint;

            const params = new URLSearchParams();

            Object.entries(config.default_query_params || {}).forEach(
              ([key, value]) => {
                params.append(key, value);
              },
            );

            if ([...params.keys()].length) {
              endpoint += `?${params.toString()}`;
            }

            if (fetchingRef.current[endpoint]) return null;

            try {
              fetchingRef.current[endpoint] = true;

              setFieldLoading((prev) => ({
                ...prev,
                [fieldUuid]: true,
              }));

              const data = await fetchWithCache(endpoint);

              const items = data?.items || [];

              return {
                fieldUuid,
                items,
                type: "master",
              };
            } finally {
              fetchingRef.current[endpoint] = false;

              setFieldLoading((prev) => ({
                ...prev,
                [fieldUuid]: false,
              }));
            }
          }
        }

        if (config.is_dependent) {
          const dependencies = config.master_dependencies || [];

          const dependencyValues = getFieldDependencies(field, allFields);

          const allDependenciesFilled = dependencyValues.every(
            (dep) => dep.value,
          );

          const preloadData = config?.is_data_visible === true;

          // if (!allDependenciesFilled && !preloadData) {
          //   return {
          //     fieldUuid,
          //     items: [],
          //     type: "dependent-clear",
          //   };
          // }
          if (config.is_visible === true && !allDependenciesFilled) {
            return {
              fieldUuid,
              items: [],
              type: "dependent-clear",
            };
          }

          // For is_data_visible fields: fire fetch independently so its options
          // appear immediately — not blocked by Promise.all of other fields
          if (preloadData) {
            // 1. Calculate the current dependency state
            const dependencyValues = getFieldDependencies(field, allFields);
            const fetchKey = JSON.stringify(dependencyValues);
            const preloadFetchKey = `preload:${fieldUuid}:${fetchKey}`; // Include dependency values in the key

            // 2. Only skip if the EXACT dependency state has already been fetched
            if (fetchedFields[fieldUuid] === preloadFetchKey) {
              return null;
            }

            let endpoint = config.source_endpoint;
            if (!endpoint) return null;

            const params = new URLSearchParams();
            Object.entries(config.default_query_params || {}).forEach(
              ([key, value]) => {
                params.append(key, value);
              },
            );

            // 3. IMPORTANT: Append dependency values to the endpoint even for preloaded fields
            dependencyValues.forEach((dep) => {
              if (dep.value) {
                params.append(dep.query_param, dep.value);
              }
            });

            endpoint += `?${params.toString()}`;

            if (fetchingRef.current[endpoint]) return null;

            fetchingRef.current[endpoint] = true;
            setFieldLoading((prev) => ({ ...prev, [fieldUuid]: true }));

            fetchWithCache(endpoint)
              .then((data) => {
                const items = data?.items || [];
                const options = items.map((item) => ({
                  input_field_uuid: fieldUuid,
                  label:
                    item.name ||
                    item.label ||
                    item?.title ||
                    item?.first_name ||
                    "Unnamed",
                  value: item.id ?? item.value,
                  option_uuid: item.option_uuid || item.id || item.value,
                }));

                // 4. Update fetchedFields with the new dependency-aware key
                setFetchedFields((prev) => ({
                  ...prev,
                  [fieldUuid]: preloadFetchKey,
                }));
                setDynamicOptions((prev) => ({
                  ...prev,
                  [fieldUuid]: { field_uuid: fieldUuid, options },
                }));
                if (hasChanges) {
                  // Check if options actually changed compared to current dynamicOptions
                  // const isDifferent = JSON.stringify(newOptionsMap) !== JSON.stringify(dynamicOptions);

                  const isDifferent = !isEqual(newOptionsMap, dynamicOptions);

                  if (isDifferent) {
                    setFetchedFields((prev) => ({
                      ...prev,
                      ...newFetchedFields,
                    }));
                    setDynamicOptions((prev) => ({
                      ...prev,
                      ...newOptionsMap,
                    }));
                    dispatch({
                      type: "bmrFbProperty/setOptions",
                      payload: newOptionsMap,
                    });
                  }
                }
                dispatch({
                  type: "bmrFbProperty/setOptions",
                  payload: { [fieldUuid]: { field_uuid: fieldUuid, options } },
                });
              })
              .finally(() => {
                fetchingRef.current[endpoint] = false;
                setFieldLoading((prev) => ({ ...prev, [fieldUuid]: false }));
              });

            return null;
          }

          const fetchKey = JSON.stringify(dependencyValues);

          if (fetchedFields[fieldUuid] === fetchKey) {
            return null;
          }

          let endpoint = config.source_endpoint;

          if (!endpoint) return null;

          const params = new URLSearchParams();

          Object.entries(config.default_query_params || {}).forEach(
            ([key, value]) => {
              params.append(key, value);
            },
          );

          dependencyValues.forEach((dep) => {
            if (dep.value) {
              params.append(dep.query_param, dep.value);
            }
          });

          endpoint += `?${params.toString()}`;

          if (fetchingRef.current[endpoint]) return null;

          try {
            fetchingRef.current[endpoint] = true;

            setFieldLoading((prev) => ({
              ...prev,
              [fieldUuid]: true,
            }));

            const data = await fetchWithCache(endpoint);

            const items = data?.items || [];

            return {
              fieldUuid,
              items,
              fetchKey,
              type: "dependent",
            };
          } finally {
            fetchingRef.current[endpoint] = false;

            setFieldLoading((prev) => ({
              ...prev,
              [fieldUuid]: false,
            }));
          }
        }
        return null;
      });
      const results = await Promise.all(promises);

      results.forEach((result) => {
        if (!result) return;

        const { fieldUuid, items, fetchKey, type } = result;

        newOptionsMap[fieldUuid] = {
          field_uuid: fieldUuid,
          options: items.map((item) => ({
            input_field_uuid: fieldUuid,
            label:
              item.name ||
              item.label ||
              item?.title ||
              item.first_name ||
              "Unnamed",
            value: item.id ?? item.value,
            option_uuid: item.option_uuid || item.id || item.value,
          })),
        };

        if (type === "master") {
          newFetchedFields[fieldUuid] = "master_fetched";
        }

        if (type === "dependent") {
          newFetchedFields[fieldUuid] = fetchKey;
        }

        if (type === "dependent-clear") {
          delete newFetchedFields[fieldUuid];
        }

        hasChanges = true;
      });

      if (hasChanges) {
        setFetchedFields((prev) => ({ ...prev, ...newFetchedFields }));
        setDynamicOptions((prev) => ({ ...prev, ...newOptionsMap }));
        dispatch({
          type: "bmrFbProperty/setOptions",
          payload: newOptionsMap,
        });
      }
    };

    processDropdowns();
  }, [currentDependencyValues]);

  //@info Submit button

  const handleValidationSubmit = async (values = {}) => {
    const answer_schema = {
      sections: formData.sections.map((section) => ({
        section_uuid: section.section_uuid,
        fields: section.fields.map((field) => ({
          input_uuid: field.input_uuid,
          value:
            values[field.input_uuid] !== undefined
              ? values[field.input_uuid]
              : field.value,
        })),
      })),
    };

    const data = { section_id: id ?? cardId, answer_schema };

    try {
      await dispatch(OnSubmitValidator(data)).unwrap();
      return data;
    } catch (error) {
      console.log("Check for the errors", error);
      let errorList = [];
      if (Array.isArray(error)) {
        errorList = error;
      } else if (error && Array.isArray(error.errors)) {
        errorList = error.errors;
      }

      if (errorList.length > 0) {
        if (errorList.length === 1) {
          const singleMsg =
            typeof errorList[0] === "string"
              ? errorList[0]
              : errorList[0]?.message || "Validation failed";
          notify.error(singleMsg);
        } else {
          notify.error(
            <ul style={{ margin: 0, paddingLeft: "1.2rem", textAlign: "left" }}>
              {errorList.map((err, index) => {
                const messageText =
                  typeof err === "string"
                    ? err
                    : err?.message || "Validation failed";
                return (
                  <li key={index} style={{ marginBottom: "2px" }}>
                    {messageText}
                  </li>
                );
              })}
            </ul>,
          );
        }
      } else {
        notify.error(error?.message || "Validation failed");
      }
    }
  };

  return (
    <div>
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          py: 1,
          px: 4,
          borderBottom: "1px solid #E4F9F2",
          backgroundColor: "#E4F9F2",
        }}
      >
        <Typography variant="h6" sx={{ fontWeight: "bold" }}>
          Template Name: {templateName?.name}
        </Typography>
        <Stack direction="row" spacing={1} alignItems="center">
          {!cardId && (
            <Button
              variant="contained"
              onClick={handlePublish}
              sx={{
                borderRadius: "0.375rem !important",
                width: "7.18rem !important",
              }}
              disabled={templateName?.status === 2}
            >
              Publish
            </Button>
          )}
          <IconButton onClick={handleCloseDrawer} sx={{ color: "black" }}>
            <CloseIcon />
          </IconButton>
        </Stack>
      </Box>

      {loading ? (
        <Box className="p-3">
          <Skeleton variant="text" width="30%" height={20} />
          <Divider />
          <Skeleton variant="text" width="100%" height={60} />
          <Divider />
          <Skeleton variant="text" width="100%" height={60} />
        </Box>
      ) : (
        <Box className="p-4">
          <DynamicFormGenerator
            formJson={formData.sections}
            formData={formData}
            setDFormData={setDynamicFormData}
            dynamicOptions={dynamicOptions}
            fieldLoading={fieldLoading}
            loading={false}
            onSubmit={handleValidationSubmit}
            setFormMethods={(methods) => {
              formMethodsRef.current = methods;
            }}
          />
          <Box display="flex" justifyContent="end" mt={2}>
            <Button
              variant="contained"
              onClick={() => formMethodsRef.current?.submitForm()}
            >
              Validate
            </Button>
          </Box>
        </Box>
      )}

      <DeleteModal
        open={openConfirmation}
        setClose={handleCloseConfirmation}
        title="Template is not submitted yet, would you like to submit"
        buttonText="Submit"
        handleSuccess={handleSubmit}
        handleRejection={handleCloseConfirmation}
        rejectButtonText="Cancel"
      />
    </div>
  );
};

export default ValidationAndSubmit;
